/**
 * IndustryQuizModal — first-visit onboarding quiz for website builder vendors.
 *
 * 6 questions, one at a time, each with a rich preview card. On completion,
 * hits /api/onboarding-quiz/submit → renders 3 template matches → clicking
 * one applies it and closes the modal.
 *
 * Auto-shows on the vendor's first visit to /website-builder unless dismissed.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowRight, ArrowLeft, Check, Sparkles, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const QUESTIONS = [
  {
    id: 'goal',       eyebrow: 'Step 1 · Your goal',       title: 'What are you building?',
    options: [
      { v: 'sell',       label: 'Sell products',   hint: 'Shop / store' },
      { v: 'showcase',   label: 'Showcase work',   hint: 'Portfolio / gallery' },
      { v: 'services',   label: 'Book services',    hint: 'Bookings / consultations' },
      { v: 'story',      label: 'Share a story',    hint: 'Blog / editorial' },
      { v: 'other',      label: 'Something else',   hint: 'Mixed / not sure' },
    ]
  },
  {
    id: 'industry',   eyebrow: 'Step 2 · Industry',         title: 'What industry are you in?',
    options: [
      { v: 'fashion',   label: 'Fashion / Retail' },
      { v: 'food',      label: 'Food / Café / Restaurant' },
      { v: 'art',       label: 'Art / Gallery / Music' },
      { v: 'wellness',  label: 'Wellness / Health' },
      { v: 'tech',      label: 'Tech / SaaS' },
      { v: 'consulting',label: 'Consulting / Services' },
      { v: 'nonprofit', label: 'Nonprofit / Cause' },
      { v: 'other',     label: 'Other' },
    ]
  },
  {
    id: 'style',      eyebrow: 'Step 3 · Style',            title: 'Pick a style mood',
    options: [
      { v: 'modern',    label: 'Modern',     hint: 'Clean, contemporary' },
      { v: 'editorial', label: 'Editorial',  hint: 'Magazine-style' },
      { v: 'minimal',   label: 'Minimal',    hint: 'Whitespace-focused' },
      { v: 'bold',      label: 'Bold',       hint: 'High contrast' },
      { v: 'playful',   label: 'Playful',    hint: 'Colorful, friendly' },
      { v: 'luxury',    label: 'Luxury',     hint: 'Refined, premium' },
    ]
  },
  {
    id: 'color',      eyebrow: 'Step 4 · Colors',           title: 'What color mood fits?',
    options: [
      { v: 'warm',    label: 'Warm',    hint: 'Cream, gold, terracotta' },
      { v: 'cool',    label: 'Cool',    hint: 'Blue, teal, sage' },
      { v: 'neutral', label: 'Neutral', hint: 'Cream, charcoal, ivory' },
      { v: 'bright',  label: 'Bright',  hint: 'Bold pops of color' },
      { v: 'dark',    label: 'Dark',    hint: 'Deep, dramatic' },
    ]
  },
  {
    id: 'features',   eyebrow: 'Step 5 · Features',         title: 'Which features matter?',
    multi: true,
    options: [
      { v: 'store',    label: 'Online Store' },
      { v: 'blog',     label: 'Blog' },
      { v: 'portfolio',label: 'Portfolio' },
      { v: 'contact',  label: 'Contact form' },
      { v: 'booking',  label: 'Booking / Appointments' },
      { v: 'newsletter',label:'Newsletter signup' },
    ]
  },
  {
    id: 'timeline',   eyebrow: 'Step 6 · Timeline',         title: 'When do you want to launch?',
    options: [
      { v: 'today',    label: 'Today',           hint: "Let's move fast" },
      { v: 'week',     label: 'This week' },
      { v: 'month',    label: 'This month' },
      { v: 'exploring',label: 'Just exploring',  hint: 'No rush' },
    ]
  },
];

export default function IndustryQuizModal({ open, onClose, onTemplateSelect }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState(null);

  const q = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;
  const currentValue = answers[q?.id];

  useEffect(() => {
    if (open) {
      setStep(0);
      setAnswers({});
      setMatches(null);
    }
  }, [open]);

  const pick = (v) => {
    if (q.multi) {
      const arr = Array.isArray(currentValue) ? currentValue : [];
      const next = arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
      setAnswers({ ...answers, [q.id]: next });
    } else {
      setAnswers({ ...answers, [q.id]: v });
      // Auto-advance single-select after 200ms
      setTimeout(() => {
        if (step < QUESTIONS.length - 1) setStep(step + 1);
      }, 200);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/onboarding-quiz/submit`, { answers }, { headers: authH() });
      setMatches(r.data.matches || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not compute matches');
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    try { await axios.post(`${API}/onboarding-quiz/skip`, {}, { headers: authH() }); } catch {}
    localStorage.setItem('wb:onboarding-quiz-seen', '1');
    onClose?.();
  };

  const finish = (tpl) => {
    localStorage.setItem('wb:onboarding-quiz-seen', '1');
    onTemplateSelect?.(tpl);
    onClose?.();
  };

  if (!open) return null;
  const canProceed = q.multi ? (Array.isArray(currentValue) && currentValue.length > 0) : !!currentValue;

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="industry-quiz-modal" onClick={skip}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-3xl border border-white/10 bg-[#171A2C] shadow-2xl overflow-hidden">
        {/* Header + progress */}
        <div className="relative p-6 pb-3 border-b border-white/8">
          <button onClick={skip} className="absolute top-4 right-4 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" data-testid="quiz-close" title="Skip quiz">
            <X size={16} />
          </button>
          {!matches && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shadow-[0_8px_24px_rgba(139,92,246,0.4)]">
                  <Sparkles size={19} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A78BFA]">{q.eyebrow}</p>
                  <h3 className="text-white font-bold text-xl font-syne mt-0.5">{q.title}</h3>
                </div>
              </div>
              <div className="mt-4 flex gap-1.5">
                {QUESTIONS.map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all ${i === step ? 'w-8 bg-[#A78BFA]' : i < step ? 'w-4 bg-[#A78BFA]/50' : 'w-4 bg-white/10'}`} />
                ))}
              </div>
            </>
          )}
          {matches && (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
                <Check size={20} className="text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300">Perfect match</p>
                <h3 className="text-white font-bold text-xl font-syne mt-0.5">3 templates picked for you</h3>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {!matches && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid={`quiz-step-${q.id}`}>
              {q.options.map((o) => {
                const active = q.multi ? (Array.isArray(currentValue) && currentValue.includes(o.v)) : (currentValue === o.v);
                return (
                  <button key={o.v} onClick={() => pick(o.v)}
                    className={`text-left px-4 py-3.5 rounded-xl border transition-all ${active ? 'border-[#A78BFA] bg-[#A78BFA]/10 shadow-[0_0_0_1px_rgba(167,139,250,0.35)]' : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}`}
                    data-testid={`quiz-option-${q.id}-${o.v}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-white font-bold text-sm">{o.label}</p>
                      {active && <Check size={14} className="text-[#A78BFA]" />}
                    </div>
                    {o.hint && <p className="text-white/50 text-[11.5px] mt-0.5">{o.hint}</p>}
                  </button>
                );
              })}
            </div>
          )}
          {matches && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="quiz-matches">
              {matches.map((t) => (
                <button key={t.id} onClick={() => finish(t)}
                  className="group text-left rounded-xl overflow-hidden border border-white/10 hover:border-[#A78BFA]/50 hover:shadow-[0_16px_40px_rgba(139,92,246,0.25)] transition-all bg-white/[0.02]"
                  data-testid={`quiz-match-${t.id}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={t.cover} alt={t.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                  </div>
                  <div className="p-3">
                    <p className="text-white font-bold text-sm">{t.name}</p>
                    <p className="text-white/50 text-[11px] uppercase tracking-wider mt-0.5">{t.industry} · {t.style}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold text-[#A78BFA] opacity-0 group-hover:opacity-100 transition-opacity">
                      Use this <ArrowRight size={10} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!matches && (
          <div className="p-5 border-t border-white/8 flex items-center justify-between gap-2 bg-white/[0.02]">
            <button onClick={skip} className="text-[11px] text-white/55 hover:text-white uppercase tracking-wider font-bold px-3 py-2" data-testid="quiz-skip">Skip quiz</button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white/70 hover:text-white text-[11px] font-bold uppercase tracking-wider" data-testid="quiz-back">
                  <ArrowLeft size={12} /> Back
                </button>
              )}
              {!isLast && (
                <button onClick={() => setStep(s => s + 1)} disabled={!canProceed}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]"
                  data-testid="quiz-next">
                  Next <ArrowRight size={12} />
                </button>
              )}
              {isLast && (
                <button onClick={submit} disabled={!canProceed || submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]"
                  data-testid="quiz-submit">
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Find my templates
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
